<template>
  <transition appear name="fade">
    <div
      v-if="badge"
      class="badge"
      :class="`badge--${backgroundColor} badge--${size}`"
      v-data-test-id="'badge'"
    >
      <component
        :is="badge.icon"
        v-if="badge.icon"
        class="badge__icon"
        :class="`badge__icon--${badge.type}`"
      />
      <div class="badge__copy" v-data-test-id="'badge-copy'">
        {{ $t(`label.${badge.translationKey}`) }}
      </div>
    </div>
  </transition>
</template>

<script>
import IconBolt from '@/components/_icons/IconBolt.svg'
import IconDot from '@/components/_icons/IconDot.svg'

export const BADGES = {
  bestseller: { type: 'bestseller', translationKey: 'bestseller' },
  exclusive: { type: 'exclusive', translationKey: 'exclusive' },
  lastChance: { type: 'last-chance', translationKey: 'last_chance' },
  limited: { type: 'limited', translationKey: 'limited' },
  new: { type: 'new', translationKey: 'new', icon: IconBolt },
  newColor: { type: 'new-color', translationKey: 'new_color', icon: IconDot },
  newColors: { type: 'new-colors', translationKey: 'new_colors' },
  newGeneration: { type: 'new-generation', translationKey: 'new_generation' },
  sellingFast: { type: 'selling-fast', translationKey: 'selling_fast' },
  soldOut: { type: 'sold-out', translationKey: 'sold_out' },
  waterproof: { type: 'waterproof', translationKey: 'waterproof' },
}

export const BACKGROUND_COLORS = {
  white: 'white',
  grey: 'grey',
  lightgrey: 'lightgrey',
  blue: 'blue',
}

const PRECEDENCE_ORDERED_BADGES = [
  BADGES.soldOut,
  BADGES.exclusive,
  BADGES.limited,
  BADGES.new,
  BADGES.newGeneration,
  BADGES.newColor,
  BADGES.newColors,
  BADGES.sellingFast,
  BADGES.lastChance,
  BADGES.waterproof,
]

export const SIZES = {
  small: 'small',
  normal: 'normal',
}

export default {
  name: 'Badge',

  props: {
    badgeTypes: {
      type: Array,
      validator(badgeTypes) {
        return badgeTypes.every(type =>
          Object.keys(BADGES).some(badgeKey => BADGES[badgeKey].type === type)
        )
      },
    },

    backgroundColor: {
      type: String,
      validator(value) {
        return Object.values(BACKGROUND_COLORS).includes(value)
      },
      default: BACKGROUND_COLORS.white,
    },

    size: {
      type: String,
      validator(size) {
        return Object.values(SIZES).includes(size)
      },
      default: SIZES.normal,
    },
  },

  computed: {
    badge() {
      return PRECEDENCE_ORDERED_BADGES.find(badge => this.badgeTypes.includes(badge.type))
    },
  },
}
</script>

<style lang="scss" scoped>
.badge {
  position: absolute;
  display: flex;
  z-index: 1;
  top: 26px;
  left: 28px;
  padding-left: 2px;
  font-size: $f-size-xxxsmall;
  font-weight: bold;
  letter-spacing: 0.35em;
  text-transform: uppercase;

  &--small {
    font-size: $f-size-tiny;
    padding: 0;
    letter-spacing: 0.25em;

    .badge__copy {
      padding: 6px 8px;
    }

    .badge__icon--new {
      width: 15px;
      height: 15px;
    }
  }

  &--white {
    background-color: transparentize($color-white, 0.15);

    .badge__copy {
      color: $color-text;
    }

    .badge__icon {
      fill: $color-text;
    }
  }

  &--grey {
    background-color: rgba(107, 107, 107, 0.85);
  }

  &--lightgrey {
    background-color: $color-grey-light-4;
  }

  &--blue {
    background-color: rgba(143, 155, 163, 0.85);
  }

  &--grey,
  &--blue {
    .badge__copy {
      color: $color-white;
    }

    .badge__icon {
      fill: $color-white;
    }
  }

  &__copy {
    padding: 9px;
    display: inline-block;
  }

  &__icon {
    position: relative;
    align-self: center;

    &--new {
      width: 17px;
      height: 17px;
      margin: 0 -4px 0 6px;
    }

    &--new-color {
      height: 6px;
      width: 6px;
      margin: 0 0 0 9px;
    }
  }
}

.fade-enter-active {
  transition: opacity $transition-time-slow;
}

.fade-leave-active {
  transition: opacity $transition-time;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
